import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import useFetch from '../../hooks/useFetch';
import { extractErrorMessage } from '../../utils/errorHandling';
import {
	ErrorMessage,
	LoadingGif,
	LoadingMessage,
	PageTitle,
	StandardContentContainer,
	StyledTable,
} from '../Shared/CommonStyles';
import { Button } from '../Shared/FormStyles';
import UserForm from './UserForm';
import FlappyImageManager from './FlappyImageManager';
import { IUser } from './types';
import { axiosInstance as axios } from '../../config/axios';
import * as S from './AdminDashboard.styles';

const AdminDashboard: React.FC = () => {
	const { user: currentUser } = useAuth();
	const [activeTab, setActiveTab] = useState<'usuaris' | 'stickers'>('usuaris');
	const [editingUser, setEditingUser] = useState<IUser | null>(null);
	const [isCreating, setIsCreating] = useState(false);
	const [isLoadingStats, setIsLoadingStats] = useState(false);
	const queryClient = useQueryClient();

	const {
		data: users,
		isLoading,
		error,
	} = useFetch<IUser[]>({
		queryKey: ['users'],
		url: '/api/users',
	});

	const handleViewStats = async () => {
		setIsLoadingStats(true);
		// Open synchronously (still inside the click → popup allowed), then
		// navigate to the report: a top-level GET can't carry the JWT header,
		// and document.write into about:blank inherits the app's strict CSP
		// (script-src 'self') — which blocks the report's inline scripts =
		// white page. One-time token + real navigation avoids both.
		const tab = window.open('', '_blank');
		if (!tab) {
			setIsLoadingStats(false);
			alert('Please allow popups to view stats.');
			return;
		}
		tab.document.write('<html><head><title>Carregant estadístiques…</title></head><body><h1>Carregant estadístiques…</h1></body></html>');
		try {
			const { data } = await axios.post<{ token: string }>(
				'/api/admin/stats-token'
			);
			tab.location.href = `/api/admin/stats-view?token=${data.token}`;
		} catch (error: any) {
			console.error(error);
			tab.close();
			alert('Failed to load stats: ' + (error.response?.statusText || error.message));
		} finally {
			setIsLoadingStats(false);
		}
	};

	const deleteMutation = useMutation<void, Error, string>({
		mutationFn: (userId: string) => axios.delete(`/api/users/${userId}`),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] });
		},
	});

	const handleDelete = async (userId: string) => {
		if (window.confirm('Segur que vols eliminar aquest usuari?')) {
			deleteMutation.mutate(userId);
		}
	};

	const handleEdit = (user: IUser) => {
		setEditingUser(user);
		setIsCreating(false);
	};

	const handleCreate = () => {
		setEditingUser(null);
		setIsCreating(true);
	};

	const handleCancel = () => {
		setEditingUser(null);
		setIsCreating(false);
	};

	const handleSuccess = () => {
		setEditingUser(null);
		setIsCreating(false);
	};

	if (isLoading) {
		return (
			<StandardContentContainer>
				<LoadingMessage>
					<LoadingGif size="large" />
				</LoadingMessage>
			</StandardContentContainer>
		);
	}

	if (error) {
		return (
			<StandardContentContainer>
				<ErrorMessage>
					Error loading users: {extractErrorMessage(error)}
				</ErrorMessage>
			</StandardContentContainer>
		);
	}

	if (isCreating) {
		return (
			<UserForm
				onSuccess={handleSuccess}
				onCancel={handleCancel}
				userToEdit={null}
			/>
		);
	}

	if (editingUser) {
		return (
			<UserForm
				onSuccess={handleSuccess}
				onCancel={handleCancel}
				userToEdit={editingUser}
			/>
		);
	}

	return (
		<StandardContentContainer>
			<PageTitle>Admin Dashboard</PageTitle>
			<S.TabBar>
				<S.Tab
					type='button'
					$active={activeTab === 'usuaris'}
					onClick={() => setActiveTab('usuaris')}
				>
					Usuaris
				</S.Tab>
				{currentUser?.roles.includes('Admin') && (
					<S.Tab
						type='button'
						$active={activeTab === 'stickers'}
						onClick={() => setActiveTab('stickers')}
					>
						Adhesius del Flappy
					</S.Tab>
				)}
			</S.TabBar>

			{activeTab === 'usuaris' ? (
				<S.TabContent>
					<S.ActionsContainer>
						<Button onClick={handleCreate}>Create New User</Button>
						<S.StatsButton
							onClick={handleViewStats}
							disabled={isLoadingStats}
						>
							{isLoadingStats ? 'Loading...' : 'View Server Stats'}
						</S.StatsButton>
					</S.ActionsContainer>
					<StyledTable>
						<thead>
							<tr>
								<th>Username</th>
								<th>Roles</th>
								<th>Unitat</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{users?.map((user) => (
								<tr key={user._id}>
									<td>{user.username}</td>
									<td>{user.roles.join(', ')}</td>
									<td>{user.unitat || 'N/A'}</td>
									<td>
										<Button onClick={() => handleEdit(user)}>
											Edit
										</Button>
										<Button
											onClick={() => handleDelete(user._id!)}
											disabled={
												deleteMutation.isPending ||
												user._id === currentUser?.userId
											}
											style={{ backgroundColor: '#dc3545' }}
										>
											Delete
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</StyledTable>
					{deleteMutation.error && (
						<ErrorMessage>
							Error deleting user: {extractErrorMessage(deleteMutation.error)}
						</ErrorMessage>
					)}
				</S.TabContent>
			) : (
				<S.TabContent>
					{/* Stickers del joc (admin only) — kept OUT of the users view */}
					<FlappyImageManager />
				</S.TabContent>
			)}
		</StandardContentContainer>
	);
};

export default AdminDashboard;